import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import type { MissDescription } from '../exercise-types/registry.ts';
import styles from './practice.module.css';

/**
 * A sitting the learner ended early — the screen for stopping mid-Lesson.
 *
 * A Lesson carried to its last Exercise closes on `/revisao`
 * (`../content/LessonReview.tsx`), which reads the record back from the
 * server. This screen is for the case that record cannot answer: the learner
 * left part-way, so there is no finished Lesson to review, and what happened
 * exists nowhere but here.
 *
 * WHAT IT REPORTS IS WHAT THIS BROWSER WATCHED HAPPEN. A Learning Session
 * never ends (FR-058) and the server keeps no per-run result, so nothing here
 * is read back from an endpoint: the shell counted the attempts it submitted
 * and the results it was handed, and this screen says exactly that and no
 * more. "de primeira" is literally true for the same reason — the loop offers
 * one submission per Exercise before advancing, so a correct answer is a
 * first-try answer by construction.
 *
 * The Lesson figure beside them is the server's: how many of that Lesson's
 * Exercises are recorded as complete, out of how many it holds.
 *
 * § 4 gives grad/success to "the one moment the system raises intensity", and
 * the shell puts this screen on it. Over that ground § 13 asks for a result
 * and ONE decision — so the two controls are a primary and a ghost, never two
 * primaries, and the way out is the one that reads as finishing.
 */

interface SessionSummaryProps {
  /** The Lesson the sitting was spent in, when the shell knows it. */
  lessonTitle: string | null;
  /** Attempts this browser submitted and had recorded, and how many were right. */
  recorded: number;
  correct: number;
  /** The Lesson's own progress, as the server reports it. Null when unknown. */
  lessonProgress: { done: number; total: number } | null;
  /** What got away, as each Exercise Type names it. May be empty. */
  misses: readonly MissDescription[];
  /** Absent when there is nothing left to practise. */
  onContinue?: () => void;
  onFinish: () => void;
}

export function SessionSummary({
  lessonTitle,
  recorded,
  correct,
  lessonProgress,
  misses,
  onContinue,
  onFinish,
}: SessionSummaryProps) {
  return (
    <div className={styles.summary} data-testid="session-summary">
      <div className={styles.summaryBody}>
        <div className={styles.summaryTitle}>
          <Badge tone="neutral">Sessão concluída</Badge>

          {/*
            The headline is the two counts, set rather than flowed. § 5.1 caps
            `display-xl` at three words a line and gives no measure to do it
            with, so the break is chosen here instead of left to a 360px
            screen to find.
          */}
          <Text as="h1" variant="display-xl">
            {recorded} {recorded === 1 ? 'exercício' : 'exercícios'},
            <br />
            {correct} {correct === 1 ? 'certo' : 'certos'}.
          </Text>

          {lessonTitle ? (
            <Text as="p" variant="subheading">
              {lessonTitle}
            </Text>
          ) : null}
        </div>

        <ul className={styles.summaryTiles}>
          <li className={styles.summaryTile}>
            <Text variant="numeric-display">{recorded}</Text>
            <Text variant="caption">{recorded === 1 ? 'exercício' : 'exercícios'}</Text>
          </li>
          <li className={styles.summaryTile}>
            <Text variant="numeric-display">{correct}</Text>
            <Text variant="caption">de primeira</Text>
          </li>
          {lessonProgress ? (
            <li className={styles.summaryTile}>
              <Text variant="numeric-display">{lessonProgress.done}</Text>
              <Text variant="caption">de {lessonProgress.total} na lição</Text>
            </li>
          ) : null}
        </ul>

        {misses.length > 0 ? (
          <section className={styles.summarySection} aria-labelledby="session-misses">
            <Text as="h2" id="session-misses" variant="subheading">
              {misses.length === 1 ? 'A que escapou' : 'As que escaparam'}
            </Text>

            <ul className={styles.summaryMisses}>
              {misses.map((miss, index) => (
                <li key={`${miss.token}-${index}`} className={styles.summaryMiss}>
                  <Text variant="token-lg">{miss.token}</Text>
                  <Text variant="body-sm">{miss.detail}</Text>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <footer className={styles.summaryActions}>
        <Button onClick={onFinish}>Terminar por hoje</Button>
        {onContinue ? (
          <Button variant="ghost" icon="play" onClick={onContinue}>
            Continuar praticando
          </Button>
        ) : null}
      </footer>
    </div>
  );
}
